import React, { useCallback, useMemo, useState } from 'react';
import { Table, Segmented, Modal } from 'antd';
import { appMessage as message } from '../utils/appMessage';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { DashboardLayout } from '../layouts/DashboardLayout';
import { fetchBuybacks, fetchBuybackDetail, prepareBuyback } from '../api/api';
import {
  Buyback, BuybackDetail, BuybackStatus, BUYBACK_STATUS_LABEL, BUYBACK_STATUS_ORDER,
} from '../types/buyback';
import { SPACING, type ThemeTokens } from '../constants/constants';
import { useThemeTokens } from '../hooks/UseThemeTokens';
import { useEntityList } from '../hooks/UseEntityList';
import { useInlineLoadError } from '../hooks/UseAsyncData';
import { AppButton } from '../components/common/AppButton';
import { BuybackStatusTag } from '../components/buyback/BuybackStatusTag';
import { BuybackCart } from '../components/buyback/BuybackCart';
import { BuybackDetailView } from '../components/buyback/BuybackDetailView';
import { AppLoadError } from '../components/common/AppLoadError';
import { TableEmptyState, buildPagination, textColumn } from '../components/common/TableHelpers';
import { formatWonOrDash } from '../utils/format';

type Filter = 'ALL' | BuybackStatus;

const won = formatWonOrDash;

// ─── 컬럼 정의 ───────────────────────────────────────────────────────────────
function buildColumns(t: ThemeTokens): ColumnsType<Buyback> {
  return [
    textColumn<Buyback>(t, '관리번호', 'managementNumber', {
      width: 130,
      color: (v) => (v ? t.textPrimary : t.textMuted),
      format: (v, r) => v ?? `#${r.id}`,
    }),
    textColumn<Buyback>(t, '접수일시', 'createdAt', {
      width: 150,
      defaultSortOrder: 'descend',
      sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
      color: t.textSecondary,
      format: (v) => dayjs(v).format('YYYY/MM/DD HH:mm'),
    }),
    {
      title: '상태', dataIndex: 'status', width: 120,
      render: (v: BuybackStatus) => <BuybackStatusTag status={v} />,
    },
    textColumn<Buyback>(t, '이름', 'name', {
      width: 110, bold: true,
      color: (v) => (v ? t.textPrimary : t.textMuted),
    }),
    textColumn<Buyback>(t, '전화번호', 'phone', {
      width: 150,
      color: (v) => (v ? t.textPrimary : t.textMuted),
    }),
    textColumn<Buyback>(t, '담긴 수량', 'itemCount', { width: 100, align: 'center' }),
    textColumn<Buyback>(t, '매입 총액', 'totalAmount', {
      width: 130, align: 'right',
      color: (v) => (v != null ? t.accent : t.textMuted),
      format: (v) => won(v),
    }),
  ];
}

// ─── BuybackPage ──────────────────────────────────────────────────────────────
export const BuybackPage: React.FC = () => {
  const t = useThemeTokens();
  const { items: data, loading, reload: load, error, retry, hasLoadedOnce, setInlineError } =
    useEntityList(fetchBuybacks, '매입 목록을 불러오지 못했습니다.');

  // 재조회 실패는 화면 인라인 배너로만 알린다 — 훅이 토스트까지 띄우면 같은 오류가 두 번
  // 보인다(2026-10-21 PR #39 사후 리뷰). 이 화면이 마운트된 동안만 토스트를 끈다 — 카드/매입
  // 훅은 여러 화면이 공유하므로, 벗어나면 다시 켜져 다른 화면은 예전처럼 토스트로 알린다.
  useInlineLoadError(setInlineError);
  const [pageSize, setPageSize] = useState(50);
  const [filter, setFilter]   = useState<Filter>('ALL');

  const [cartOpen, setCartOpen]     = useState(false);
  const [cartDetail, setCartDetail] = useState<BuybackDetail | null>(null);
  const [viewOpen, setViewOpen]     = useState(false);
  const [viewDetail, setViewDetail] = useState<BuybackDetail | null>(null);

  // 상태별 건수(필터 라벨용)
  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: data.length };
    for (const s of BUYBACK_STATUS_ORDER) c[s] = 0;
    data.forEach((b) => { c[b.status] = (c[b.status] ?? 0) + 1; });
    return c;
  }, [data]);

  const filtered = useMemo(
    () => (filter === 'ALL' ? data : data.filter((b) => b.status === filter)),
    [data, filter],
  );

  // 첫 조회부터 실패해 보여 줄 목록이 없는 상태. 재조회만 실패했으면 useAsyncData 가 이전
  // 결과를 그대로 두므로 표를 유지하고 위에 한 줄 배너만 붙인다(2026-10-20 리뷰 후속 —
  // 예전엔 오류면 표를 통째로 AppLoadError 로 바꿔 이전 결과까지 사라졌다).
  //
  // `data.length === 0` 으로 판단하면 안 된다 — 진짜 0건이 정상 응답인 화면이라, 0건을 한 번
  // 받은 뒤 재조회가 실패하면 보여 줄 이전 결과(0건)가 있는데도 전체 오류 화면이 됐다.
  // "성공한 조회가 있었는지"(`hasLoadedOnce`)로 판단한다(2026-10-21 PR #39 사후 리뷰).
  const failedEmpty = !!error && !hasLoadedOnce;

  const segmentOptions = useMemo(
    () => [
      // 조회 실패면 0건으로 단정하지 않는다 — 실패와 "진짜 0건"은 다른 상태다(2026-10-20 코덱 3차 지적).
      { label: failedEmpty ? '전체 —' : `전체 ${counts.ALL}`, value: 'ALL' as Filter },
      ...BUYBACK_STATUS_ORDER.map((s) => ({
        label: failedEmpty ? `${BUYBACK_STATUS_LABEL[s]} —` : `${BUYBACK_STATUS_LABEL[s]} ${counts[s] ?? 0}`,
        value: s as Filter,
      })),
    ],
    [counts, failedEmpty],
  );

  // ── 행 클릭: 상태에 따라 확인/카트/상세 ─────────────────────────────────────
  const openCart = useCallback((detail: BuybackDetail) => {
    setCartDetail(detail);
    setCartOpen(true);
  }, []);

  const openView = useCallback(async (id: number) => {
    try {
      const detail = await fetchBuybackDetail(id);
      setViewDetail(detail);
      setViewOpen(true);
    } catch { message.error('상세를 불러오지 못했습니다.'); }
  }, []);

  const handleRow = useCallback((record: Buyback) => {
    if (record.status === 'NEW_ORDER') {
      Modal.confirm({
        title: '매입을 진행하시겠습니까?',
        content: `${record.name ?? '고객'} 님의 매입 오더를 준비중으로 전환하고 카드를 담습니다.`,
        okText: '진행', cancelText: '취소',
        onOk: async () => {
          try {
            const detail = await prepareBuyback(record.id);
            load();
            openCart(detail);
          } catch { message.error('준비중 전환 중 오류가 발생했습니다.'); }
        },
      });
    } else if (record.status === 'PREPARING' || record.status === 'READY') {
      // 준비 완료 상태도 카트로 열어 가격·수량·카드 구성을 그 자리에서 수정할 수 있게 한다.
      fetchBuybackDetail(record.id).then(openCart).catch(() => message.error('상세를 불러오지 못했습니다.'));
    } else {
      openView(record.id); // COMPLETED / CANCELLED (읽기 전용 내역)
    }
  }, [load, openCart, openView]);

  const onSaved = useCallback((updated: BuybackDetail) => {
    setCartDetail(updated);
    load();
  }, [load]);

  return (
    <DashboardLayout title="매입 관리">
      <div style={{ marginBottom: SPACING.md, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACING.sm, flexWrap: 'wrap' }}>
        {/* 상태 탭 6개는 390px 한 줄에 다 못 들어간다 — 잘리는 대신 가로로 스크롤한다
            (2026-10-20 코덱 3차 지적). */}
        <div style={{ minWidth: 0, maxWidth: '100%', overflowX: 'auto' }}>
          <Segmented options={segmentOptions} value={filter} onChange={(v) => setFilter(v as Filter)} />
        </div>
        <AppButton icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ marginLeft: 'auto' }}>새로고침</AppButton>
      </div>

      {error && !failedEmpty && (
        <AppLoadError inline message={error.message} error={error} onRetry={retry} />
      )}
      {failedEmpty ? (
        <AppLoadError message={error?.message} error={error} onRetry={retry} />
      ) : (
        <Table
          rowKey="id"
          columns={buildColumns(t)}
          dataSource={filtered}
          loading={loading}
          bordered
          onRow={(record) => ({ onClick: () => handleRow(record), style: { cursor: 'pointer' } })}
          pagination={buildPagination(pageSize, setPageSize)}
          scroll={{ x: 'max-content' }}
          size="small"
          locale={{ emptyText: <TableEmptyState icon="📇" message="매입 오더가 없습니다" t={t} /> }}
        />
      )}

      <BuybackCart open={cartOpen} detail={cartDetail} onClose={() => setCartOpen(false)} onSaved={onSaved} />
      <BuybackDetailView open={viewOpen} detail={viewDetail} onClose={() => setViewOpen(false)} />
    </DashboardLayout>
  );
};
